import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, SlidersHorizontal, Loader2 } from "lucide-react";

export default function LandingsAdmin() {
  const [catalog, setCatalog] = useState([]);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [keys, setKeys] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/landings").then(({ data }) => {
    setCatalog(data.catalog || []); setUsers(data.users || []);
  }), []);
  useEffect(() => { load(); }, [load]);

  const open = (u) => { setEdit(u); setKeys(u.keys || []); };
  const toggle = (k) => setKeys((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
  const allKeys = catalog.map((c) => c.key);
  const setAll = () => setKeys(keys.length === allKeys.length ? [] : allKeys);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/landings/user/${edit.id}`, { keys });
      toast.success("Autorizzazioni salvate");
      setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const nameOf = (k) => catalog.find((c) => c.key === k)?.name || k;

  return (
    <div className="space-y-6" data-testid="landings-admin">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Landing</h1>
        <p className="mt-1 text-muted-foreground">
          Le landing sono un canale <b>diretto di Assietica</b>. Autorizza prodotto per prodotto gli utenti che possono
          vederle e condividerle dal portale. Di default nessun utente è autorizzato.
        </p>
      </div>

      <div className="grid gap-3">
        {users.map((u) => (
          <Card key={u.id} data-testid={`landing-user-${u.id}`} className="rounded-xl border-border/60 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sage text-forest">
                  <Megaphone className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-base font-semibold text-forest">{u.name}</h3>
                    <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{u.email}{u.codice_collab ? ` · ${u.codice_collab}` : ""}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(u.keys || []).length === 0 ? (
                      <span className="text-xs text-muted-foreground">Nessuna landing autorizzata</span>
                    ) : u.keys.map((k) => (
                      <Badge key={k} className="bg-forest/10 text-forest text-[11px]">{nameOf(k)}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => open(u)} data-testid={`landing-manage-${u.id}`} className="gap-1.5">
                <SlidersHorizontal className="h-3.5 w-3.5" /> Gestisci landing
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Landing autorizzate — {edit?.name}</DialogTitle>
            <DialogDescription>Seleziona le landing che questo utente potrà vedere e condividere dal portale.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Canale diretto Assietica.</p>
            <button type="button" onClick={setAll} className="text-xs font-medium text-terracotta hover:underline" data-testid="landing-toggle-all">
              {keys.length === allKeys.length ? "Deseleziona tutte" : "Seleziona tutte"}
            </button>
          </div>
          <div className="space-y-1">
            {catalog.map((c) => (
              <label key={c.key} data-testid={`landing-opt-${c.key}`} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 p-2.5 hover:bg-sage/30">
                <Checkbox checked={keys.includes(c.key)} onCheckedChange={() => toggle(c.key)} className="mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-forest">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.descrizione}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <span className="text-xs text-muted-foreground">{keys.length} / {allKeys.length} selezionate</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
              <Button onClick={save} disabled={busy} data-testid="landing-save" className="gap-1.5 bg-forest hover:bg-forest/90">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
